import type {
  EndEvent,
  IncomingAckEvent,
  TerminateOptions,
} from 'jssip/lib/RTCSession';
import type { URI } from 'jssip/lib/URI';
import type { IncomingResponse } from 'jssip/lib/SIPMessage';

import { determineRtcFailureType } from 'features/sip/helpers/';
import {
  sendAnalyticsError,
  sendAnalyticsInfo,
} from 'features/analytics/helpers';
import type { Call } from 'features/call/call-base/interfaces';
import { RtcFailureType, SipLogMessages } from 'features/sip/enums';
import type { AppDispatch } from 'features/app/store/store';

export const sipConnectingAnalytics = () => {
  return sendAnalyticsInfo({
    Method: 'SipEventStatus.CONNECTING',
    Message: SipLogMessages.SIP_CONNECTING,
  });
};

export const sipConnectedAnalytics = () => {
  return sendAnalyticsInfo({
    Method: 'SipEventStatus.CONNECTED',
    Message: SipLogMessages.SIP_CONNECTION_SUCCESS,
  });
};

export const sipDisconnectedAnalytics = () => {
  return sendAnalyticsInfo({
    Method: 'SipEventStatus.DISCONNECTED',
    Message: SipLogMessages.SIP_CONNECTION_LOST,
  });
};

export const sipRegisteredAnalytics = () => {
  return sendAnalyticsInfo({
    Method: 'SipEventStatus.REGISTERED',
    Message: SipLogMessages.SIP_REGISTERED,
  });
};

export const sipUnregisteredAnalytics = () => {
  return sendAnalyticsInfo({
    Method: 'SipEventStatus.UNREGISTERED',
    Message: SipLogMessages.SIP_UNREGISTERED,
  });
};

export const sipRegistrationFailedAnalytics = () => {
  return sendAnalyticsInfo({
    Method: 'SipEventStatus.REGISTRATION_FAILED',
    Message: SipLogMessages.SIP_REGISTRATION_FAILED,
  });
};

export const rtcSessionConfirmedAnalytics = (event: IncomingAckEvent) => {
  return sendAnalyticsInfo({
    Method: 'rtcSessionConfirmed',
    Originator: event.originator,
  });
};

export const rtcSessionFailedAnalytics = (
  methodName: string,
  event: EndEvent
) => {
  const failureType = determineRtcFailureType(event);
  const { cause, originator, message } = event;

  // For technical failures, include more detailed information in the message
  let detailedMessage = `RTC Session Failed from ${originator} because of ${cause}`;

  // Add more detailed information for technical failures
  if (
    [
      RtcFailureType.REQUEST_TIMEOUT,
      RtcFailureType.WEBRTC_ERROR,
      RtcFailureType.TRANSPORT_ERROR,
      RtcFailureType.NO_ACK,
      RtcFailureType.SERVER_ERROR_500,
      RtcFailureType.REJECTED,
    ].includes(failureType)
  ) {
    const statusCode = (message as IncomingResponse)?.status_code;
    detailedMessage = `TECHNICAL FAILURE: ${failureType} from ${originator}. Cause: ${cause}${statusCode ? `. Status: ${statusCode}` : ''}`;
    return sendAnalyticsError({
      Method: methodName,
      Event: {
        originator,
        cause,
        statusCode: statusCode ?? null,
      },
      Message: detailedMessage,
      FailureType: failureType,
    });
  } else {
    return sendAnalyticsInfo({
      Method: methodName,
      Event: {
        cause,
        originator,
      },
      FailureType: failureType,
      Message: detailedMessage,
    });
  }
};

export const rtcSessionUserMediaFailedAnalytics = (methodName: string) => {
  return sendAnalyticsError({
    Method: methodName,
    Message: SipLogMessages.RTC_SESSION_GET_USER_MEDIA_FAILED_EVENT,
    FailureType: RtcFailureType.USER_MEDIA_FAILED,
  });
};

export const rtcSessionTerminateAnalytics = (
  methodName: string,
  terminateOptions: TerminateOptions | void,
  sessionTerminate: TerminateOptions
) => {
  return sendAnalyticsInfo({
    Method: methodName,
    SessionTerminate: sessionTerminate,
    TerminateOptions: terminateOptions,
    Message: SipLogMessages.RTC_SESSION_TERMINATE_ACTION,
  });
};

export const rtcSessionReferAnalytics = (
  methodName: string,
  uri: string | URI
) => {
  return sendAnalyticsInfo({
    Method: methodName,
    Uri: uri,
    Message: SipLogMessages.RTC_SESSION_REFER_ACTION,
  });
};

export const retryRegisterAnalytics = (
  retryCount: number,
  isDeafConnected: boolean
) => {
  return sendAnalyticsInfo({
    event: 'sip_register_retry',
    retryCount: retryCount,
    Method: 'retryRegisterOnSipServer',
    deafConnected: isDeafConnected,
    message: 'retry registration on sip server',
  });
};

export const sendPartialCallAnalytics = (
  dispatch: AppDispatch,
  call: Partial<Call> | undefined,
  callId: number
) => {
  if (!call) {
    return;
  }
  const { EndpointMacAddress, DeafIpTraceback, ...rest } = call;
  const maskPattern = /^.{1,7}/;

  const maskedCall = {
    ...rest,
    EndpointMacAddress:
      EndpointMacAddress?.replace(maskPattern, 'xxxxxxx') ?? '',
    DeafIpTraceback: DeafIpTraceback?.replace(maskPattern, 'xxxxxxx') ?? '',
  };
  dispatch(
    sendAnalyticsInfo({
      Method: 'updatePartialCall',
      CallId: callId,
      Call: maskedCall,
      Message: 'update partial call',
    })
  );
};
